/**
 * @fileoverview Clearing missing-notes notices, as every list shows and sends
 * it (backend `services/notes_clearances`, docs/notes-clearances.md).
 *
 * A past session nobody wrote up owes notes on both sides. An organization
 * admin can clear that notice; an instructor *asks* to clear a meeting of an
 * organization's class and clears only what no organization tracks. A row
 * carries the record as `notes_clearance`:
 *
 * - `status: "requested"` — an instructor asked; still owing, tagged as asked.
 * - `status: "cleared"` — nobody owes notes for it any more.
 *
 * Selections larger than the server's page are sent in pages, one after
 * another, and their answers merged, so "select all" on a long list works.
 */

import { toast } from "sonner";

export const CLEARED = "cleared";
export const REQUESTED = "requested";

/** The server's page: `NOTES_CLEAR_BATCH` in backend/models.py. */
export const PAGE_SIZE = 200;

export const isCleared = (row) => row?.notes_clearance?.status === CLEARED || row?.session_status === "notes cleared";
export const isRequested = (row) => row?.notes_clearance?.status === REQUESTED;

/** The tag a row carries for its clearance, or null. */
export function clearanceTag(clearance) {
  if (!clearance) return null;
  if (clearance.status === REQUESTED) return { label: "Clear requested", accent: true };
  if (clearance.status === CLEARED) return { label: "Notes cleared", accent: false };
  return null;
}

/** One line on who did what: "Asked by Robin Vance · Field trip". */
export function clearanceLine(clearance) {
  if (!clearance) return null;
  if (clearance.status === REQUESTED) {
    return [clearance.requested_by_name ? `Asked by ${clearance.requested_by_name}` : "Clear requested", clearance.reason]
      .filter(Boolean)
      .join(" · ");
  }
  if (clearance.status === CLEARED) {
    const by = clearance.cleared_by_name ? `Cleared by ${clearance.cleared_by_name}` : "Notes cleared";
    return [by, clearance.note || clearance.reason].filter(Boolean).join(" · ");
  }
  return null;
}

/** How an organization's lists name a meeting to the server. */
export const orgRef = (row) => ({
  class_id: row.class_id,
  occurrence_id: row.occurrence_id || null,
  date: row.date || row.scheduled_date,
});

/** Split a list into the server's pages. */
export function pages(items, size = PAGE_SIZE) {
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * Send a selection page by page and merge the answers. A page that changed
 * nothing comes back as a 409 `nothing_changed` carrying its skips; that is an
 * answer, not a failure, so it is merged like any other. Anything else stops
 * and is thrown, with what had already gone through attached as `partial`.
 *
 * @param {Array} items
 * @param {(page: Array) => Promise<{data: object}>} send
 * @param {string[]} fields the result arrays to merge, e.g. ["cleared"]
 */
export async function sendInPages(items, send, fields) {
  const merged = Object.fromEntries([...fields, "skipped"].map((f) => [f, []]));
  merged.approved = 0;
  for (const page of pages(items)) {
    let data;
    try {
      ({ data } = await send(page));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "nothing_changed") {
        merged.skipped.push(...(detail.skipped || []));
        continue;
      }
      err.partial = merged;
      throw err;
    }
    for (const f of fields) merged[f].push(...(data?.[f] || []));
    merged.skipped.push(...(data?.skipped || []));
    merged.approved += data?.approved || 0;
  }
  return merged;
}

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const REASONS = {
  already_cleared: "already cleared",
  already_requested: "already asked about",
  not_owed: "no longer owing notes",
  has_notes: "already written up",
  not_yet: "not happened yet",
  cancelled: "cancelled",
  not_found: "no longer on the schedule",
  not_cleared: "not cleared",
  not_requested: "no longer waiting on an answer",
  not_yours: "cleared by your organization",
  changed: "changed while you were choosing",
  // Archiving and deleting from the organization's lists (org_session_marks).
  already_archived: "already archived",
  already_deleted: "already deleted",
  not_archived: "not archived",
};

/** "2 skipped: already cleared, not happened yet". */
export function skippedLine(skipped) {
  if (!skipped?.length) return undefined;
  const why = [...new Set(skipped.map((s) => REASONS[s.reason] || s.reason))].join(", ");
  return `${plural(skipped.length, "session")} skipped: ${why}`;
}

/**
 * Toast what a clear did. `done` sessions changed; nothing at all is an
 * error-toned message rather than a success.
 */
export function toastResult(message, done, skipped) {
  if (done > 0) toast.success(message, { description: skippedLine(skipped) });
  else toast.error("Nothing changed", { description: skippedLine(skipped) || "Refresh and try again." });
}

export { plural };
